export type FieldKind = 'text' | 'textarea' | 'url' | 'date' | 'time' | 'number' | 'image' | 'bool'

export interface FieldSchema {
  key: string
  label: string
  kind: FieldKind
  bilingual?: boolean        // if true, render IT/EN pair using key_it / key_en
  required?: boolean
  placeholder?: string
  small?: boolean            // narrower input in the form grid
}

export interface EntitySchema {
  id: string
  label: string
  summary: (row: Record<string, unknown>) => string   // one-line summary in list view
  fields: FieldSchema[]
  primaryField: string                                // used for a tall first column in list
  defaultRow: Record<string, unknown>
}

export const schemas: Record<string, EntitySchema> = {
  identita: {
    id: 'identita',
    label: 'Identità',
    summary: (r) => String(r.label_it ?? ''),
    primaryField: 'label_it',
    fields: [
      { key: 'label',   label: 'Label',   kind: 'text',     bilingual: true, required: true },
      { key: 'body',    label: 'Testo',   kind: 'textarea', bilingual: true, required: true },
      { key: 'sort_order', label: 'Ordine', kind: 'number', small: true },
    ],
    defaultRow: { label_it: '', label_en: '', body_it: '', body_en: '', sort_order: 0 },
  },
  singoli: {
    id: 'singoli',
    label: 'Singoli',
    summary: (r) => `${r.title} — ${r.original_artist ?? ''}`,
    primaryField: 'title',
    fields: [
      { key: 'title',           label: 'Titolo',       kind: 'text', required: true },
      { key: 'original_artist', label: 'Autore orig.', kind: 'text' },
      { key: 'cover_image_key', label: 'Cover',        kind: 'image' },
      { key: 'spotify_url',     label: 'Spotify URL',  kind: 'url' },
      { key: 'youtube_url',     label: 'YouTube URL',  kind: 'url' },
      { key: 'apple_url',       label: 'Apple URL',    kind: 'url' },
      { key: 'release_date',    label: 'Data uscita',  kind: 'date', small: true },
      { key: 'note',            label: 'Note',         kind: 'textarea', bilingual: true },
      { key: 'sort_order',      label: 'Ordine',       kind: 'number', small: true },
      { key: 'published',       label: 'Pubblicato',   kind: 'bool' },
    ],
    defaultRow: {
      title: '', original_artist: '', cover_image_key: '',
      spotify_url: '', youtube_url: '', apple_url: '',
      release_date: null, note_it: '', note_en: '',
      sort_order: 0, published: 1,
    },
  },
  gallery: {
    id: 'gallery',
    label: 'Gallery',
    summary: (r) => String(r.image_key ?? ''),
    primaryField: 'image_key',
    fields: [
      { key: 'image_key',  label: 'Immagine',  kind: 'image', required: true },
      { key: 'alt',        label: 'Alt text',  kind: 'text', bilingual: true },
      { key: 'caption',    label: 'Didascalia', kind: 'text', bilingual: true },
      { key: 'sort_order', label: 'Ordine',    kind: 'number', small: true },
      { key: 'published',  label: 'Pubblicato', kind: 'bool' },
    ],
    defaultRow: { image_key: '', alt_it: '', alt_en: '', caption_it: '', caption_en: '', sort_order: 0, published: 1 },
  },
  repertorio: {
    id: 'repertorio',
    label: 'Repertorio',
    summary: (r) => String(r.artist ?? ''),
    primaryField: 'artist',
    fields: [
      { key: 'artist',     label: 'Autore', kind: 'text', required: true },
      { key: 'sort_order', label: 'Ordine', kind: 'number', small: true },
    ],
    defaultRow: { artist: '', sort_order: 0 },
  },
  date_live: {
    id: 'date_live',
    label: 'Date live',
    summary: (r) => `${r.event_date} · ${r.venue}, ${r.city}`,
    primaryField: 'event_date',
    fields: [
      { key: 'event_date',  label: 'Data',    kind: 'date', required: true, small: true },
      { key: 'event_time',  label: 'Ora',     kind: 'time', small: true },
      { key: 'venue',       label: 'Venue',   kind: 'text', required: true },
      { key: 'city',        label: 'Città',   kind: 'text', required: true },
      { key: 'country',     label: 'Paese',   kind: 'text', small: true },
      { key: 'tickets_url', label: 'Link info/biglietti', kind: 'url' },
      { key: 'info',        label: 'Info',    kind: 'textarea', bilingual: true },
      { key: 'sort_order',  label: 'Ordine',  kind: 'number', small: true },
      { key: 'published',   label: 'Pubblicato', kind: 'bool' },
    ],
    defaultRow: {
      event_date: '', event_time: null, venue: '', city: '', country: 'IT',
      tickets_url: '', info_it: '', info_en: '', sort_order: 0, published: 1,
    },
  },
  videos: {
    id: 'videos',
    label: 'Video',
    summary: (r) => String(r.title_it ?? r.youtube_id),
    primaryField: 'title_it',
    fields: [
      { key: 'youtube_id',      label: 'YouTube ID', kind: 'text', required: true, placeholder: 'es. dQw4w9WgXcQ' },
      { key: 'title',           label: 'Titolo',     kind: 'text', bilingual: true, required: true },
      { key: 'description',     label: 'Descrizione', kind: 'textarea', bilingual: true },
      { key: 'thumb_image_key', label: 'Thumbnail custom', kind: 'image' },
      { key: 'sort_order',      label: 'Ordine', kind: 'number', small: true },
      { key: 'published',       label: 'Pubblicato', kind: 'bool' },
    ],
    defaultRow: {
      youtube_id: '', title_it: '', title_en: '',
      description_it: '', description_en: '', thumb_image_key: '',
      sort_order: 0, published: 1,
    },
  },
}
